import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { css } from '@compiled/react';

import { fg } from '@confluence/feature-gating';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import {
	getRendererAnnotationEventEmitter,
	getEditorAnnotationEventEmitter,
} from '@confluence/annotation-event-emitter';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import type { CommentData } from '@confluence/comments-data';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { CommentBatchList } from '@confluence/comment/entry-points/CommentBatchList';
import { getSortedCommentThreads } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	SortValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import {
	ExperienceTrackerContext,
	SELECT_INLINE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';

import { getCommentThreadsToBatch } from '../helper/commentThreadHelper';

import { CommentThread } from './CommentThread';

type CommentPanelListProps = {
	commentThreads: CommentData[];
	supportedTopLevelActions: CommentAction[];
	flags?: FlagsStateContainer;
	onAllCommentsRendered?: () => void;
	onCommentBatchRendered?: () => void;
};

const listStyles = css({
	overflowY: 'auto',
	overflowX: 'hidden',
	flexGrow: 1,
	display: 'block',
});

// this component holds a list of comments
export const CommentsPanelList = ({
	commentThreads,
	supportedTopLevelActions,
	flags,
	onAllCommentsRendered,
	onCommentBatchRendered,
}: CommentPanelListProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();
	const pageClass = usePageClass();
	const { currentSort, currentlySelectedThreadKey } = useCommentsPanelState();
	const { setCurrentlyRenderedThreads } = useCommentsPanelActions();

	const eventEmitter =
		pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
			? getEditorAnnotationEventEmitter()
			: getRendererAnnotationEventEmitter();

	const sortedCommentThreads = useMemo(
		() => getSortedCommentThreads(currentSort, commentThreads),
		[commentThreads, currentSort],
	);

	// Whenever the list that we're rendering here changes, we want to update the list that the keyboard shortcuts are listening to
	useEffect(() => {
		setCurrentlyRenderedThreads(
			sortedCommentThreads.map((thread) => ({ ...thread.location, id: thread.id })),
		);

		return () => {
			// When this component is unmounted, we want to clear the list that the keyboard shortcuts are listening to
			setCurrentlyRenderedThreads([]);
		};
	}, [sortedCommentThreads, setCurrentlyRenderedThreads]);

	const actionsToShowUsers =
		pageMode === PageMode.EDIT
			? supportedTopLevelActions.filter((action) => action !== 'delete')
			: supportedTopLevelActions;

	// Props we want the batch renderer to pass through that aren't comment specific
	const passThroughProps = {
		// When sorting by MOST_RECENT, the comment type separator should never be rendered
		...(currentSort === SortValues.MOST_RECENT ? { shouldRenderCommentTypeSeparator: false } : {}),
		supportedTopLevelActions: actionsToShowUsers,
		eventEmitter,
		pageMode,
		flags,
		onLastThreadRendered: onAllCommentsRendered,
		onCommentBatchRendered,
	};

	const lastSelected = useRef(currentlySelectedThreadKey);
	const selectedInlineCommentParentThread = commentThreads.find(
		(thread) =>
			'inlineMarkerRef' in thread.location &&
			thread.location.inlineMarkerRef === currentlySelectedThreadKey,
	);
	const isActiveInlineComment =
		selectedInlineCommentParentThread &&
		!selectedInlineCommentParentThread.wasRemovedByAnotherUser &&
		'inlineResolveProperties' in selectedInlineCommentParentThread.location &&
		selectedInlineCommentParentThread.location.inlineResolveProperties?.resolved === false;

	if (lastSelected.current !== currentlySelectedThreadKey) {
		// The selected annotation has changed, so we need to update the last selected
		lastSelected.current = currentlySelectedThreadKey;

		if (currentlySelectedThreadKey && isActiveInlineComment) {
			// If the selected annotation has changed to a new one, we need to start the show comment experience
			selectInlineCommentCompoundExperience.showComment.start({
				pageClass,
				annotationId: currentlySelectedThreadKey,
				entryPoint: 'unknown:CommentsPanelList',
			});
		} else {
			// If an non inline comment or non active inline comment is selected,
			// we abort any running show comment experience
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'Non inline comment being shown',
			});
		}
	}

	useEffect(() => {
		return () => {
			// If an inline comment is in the process of being shown as the sidebar closes,
			// we abort any running show comment experience.
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'Comment sidebar dismissed',
			});
		};
	}, []);

	useEffect(() => {
		if (!isActiveInlineComment) {
			return;
		}

		const initialTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;

		setTimeout(() => {
			requestAnimationFrame(() => {
				const currentTaskId = experienceTracker.getExperienceState(
					SELECT_INLINE_COMMENT_EXPERIENCE,
				)?.taskId;

				if (!currentTaskId) {
					// There is no running experience, no need to abort.
					return;
				}

				if (initialTaskId !== currentTaskId) {
					// The experience has been restarted or has otherwise ended.
					return;
				}

				if (currentlySelectedThreadKey !== lastSelected.current) {
					return;
				}

				const selectedSidebarThread = document.querySelector(
					`[data-selected-comment-thread="${currentlySelectedThreadKey}"]`,
				);

				if (!selectedSidebarThread) {
					selectInlineCommentCompoundExperience.showComment.fail(
						new Error('No selected comment thread found'),
					);
				} else {
					const isVisible = selectedSidebarThread.checkVisibility();

					if (!isVisible) {
						selectInlineCommentCompoundExperience.showComment.fail(
							new Error('Selected comment thread is not visible'),
						);
					}

					if (fg('cc_comments_check_annotation_visible_on_completion')) {
						selectInlineCommentCompoundExperience.showComment.sidePanelComplete();
					} else {
						selectInlineCommentCompoundExperience.showComment.complete(
							'Comment Sidebar inline comment selected',
						);
					}
				}
			});
		}, 1000);
	}, [currentlySelectedThreadKey, isActiveInlineComment, experienceTracker]);

	return (
		<div
			id="comments-panel-list-container"
			data-testid="comments-panel-list-container"
			css={listStyles}
		>
			<CommentBatchList
				commentsList={sortedCommentThreads}
				passThroughProps={passThroughProps}
				Component={CommentThread}
				getElementsToBatch={getCommentThreadsToBatch}
			/>
		</div>
	);
};
